import { Token } from "@umi/types/token";
import _ from "lodash";
import { Address, parseGwei, Chain as ViemChain, zeroAddress } from "viem";
import {
  abstract,
  apeChain,
  arbitrum,
  arc,
  avalanche,
  base,
  berachain,
  bsc,
  hyperliquid,
  ink,
  mainnet,
  megaeth,
  monad,
  plasma,
  polygon,
  robinhood,
  ronin,
  sei,
  shape,
  somnia,
  soneium,
  sophon
} from "viem/chains";
import { getCrossPlatformInfo } from "./crossplatform";
import { UnsupportedProviderError } from "./errors/chain";
import { EvmManager } from "./managers/evm";
import { EthMethod } from "./managers/transports/methods";


const { platform } = getCrossPlatformInfo();

export const PROVIDERS = ["drpc", "ankr", "sequencer", "public", "umi"] as const;
export type RpcProvider = (typeof PROVIDERS)[number];

export type EthSendCapability = {
  sendRawTransactionConditional?: boolean;
};

export type EthSendCapabilities = {
  [P in RpcProvider]?: EthSendCapability;
};

export type ShotgunMode = "conditional" | "regular";

export type Shotgun = {
  shots: number;
  interval: number;
  mode: ShotgunMode;
  // ms added to the deadline for the last shot. Positive fires past T0.
  targetShift?: number;
};

export type Contract = "Disperse" | "Lens" | "Bundler" | "AccountDelegate";

/**
 * EIP-7825 per-transaction gas cap: 2^24 = 16,777,216 gas.
 * Adopted by Ethereum (Fusaka), OP Stack (Base Azul / Karst),
 * BNB Smart Chain (BEP-652) and Berachain, among others.
 * @see https://eips.ethereum.org/EIPS/eip-7825
 */
export const EIP_7825_TX_GAS_CAP = 16_777_216n; // 2^24



// Provider rate limits and public RPC rate limits are separate.

type RpcRateLimitsOverride = {
  scope?: "chain" | "global";
  mode?: "request" | "operation";
  windowSize?: number;
  defaultLimit?: number;
  methods?: {
    [M in EthMethod]?: number | { windowSize?: number; limit?: number };
  };
}

export type RpcRateLimit = {
  limit: number;
  windowSize: number;
  mode: "request" | "operation";
}

type RpcRateLimits =  {
  scope: "chain" | "global";
  mode: "request" | "operation";
  windowSize: number;
  defaultLimit: number;
  methods: {
    [M in EthMethod]?: number | { windowSize?: number; limit?: number };
  };
  getMethodRateLimit: (method: EthMethod) => RpcRateLimit;
}

const buildRateLimits = (params?: RpcRateLimitsOverride): RpcRateLimits=> {

  const scope = params?.scope ?? "chain";
  const mode = params?.mode ?? "request";
  const windowSize = params?.windowSize ?? 1000;
  const defaultLimit = params?.defaultLimit ?? 50;
  const methods = params?.methods ?? {};

  return {
    scope,
    mode,
    windowSize,
    defaultLimit,
    methods,
    getMethodRateLimit: (method: EthMethod) => {
      const entry = methods[method];
      if (typeof entry === "number") return { limit: entry, windowSize, mode };
      if (entry) {
        return {
          limit: entry.limit ?? defaultLimit,
          windowSize: entry.windowSize ?? windowSize,
          mode,
        };
      }
      return { limit: defaultLimit, windowSize, mode };
    }
  };
};


const parseQuota = (raw: string | undefined) => {
  if (raw == null || raw === "") return Infinity;
  const n = Number(raw);
  if (!Number.isFinite(n) || n <= 0) return Infinity;
  return n;
};


export type IconString = `icon:${string}` | `url:${string}` | `base64:${string}` | `file:${string}` | `svg:${string}`;
export type StableCoinSymbol = "USDC" | "USDT";
type StableCoinContract = { symbol: StableCoinSymbol; icon: IconString };

/** VM / address family for a wallet (extend when adding non-EVM chains). */
export type ChainArch = "EVM";

export type Chain<
A extends ChainArch = ChainArch,
T extends string = string,
B extends boolean = boolean,
> = {
  readonly arch: A;
  readonly tag: T;
  readonly name: string;
  readonly logo: IconString;
  readonly logoDark: IconString;
  readonly blockTime: number; // Average block time in milliseconds
  readonly subBlockTime?: number; // Sub-block / pre-confirmation interval in ms (e.g. Flashblocks, mini-blocks)
  readonly l2: A extends "EVM" ? boolean : never;
  readonly id: A extends "EVM" ? number : never;
  readonly fixedBaseFeePerGas?: A extends "EVM" ? bigint : never; // Fixed base fee per gas, if applicable
  readonly minPriorityFeePerGas: A extends "EVM" ? bigint : never; // Minimum priority fee per gas, if applicable
  readonly txOrderingStrategy: A extends "EVM" ? "FCFS" | "GAS" : never;
  readonly testnet: B;
  // Typed as the base viem `Chain` (not the specific `V`) so `typeof CHAINS[number]`
  // does not fan out into 20+ distinct viem chain types. Consumers create clients
  // with the base chain shape; the concrete chain object is still assigned at runtime.
  readonly viem: A extends "EVM" ? ViemChain : never;
  readonly opStack: A extends "EVM" ? boolean : never;
  readonly zkSync: A extends "EVM" ? boolean : never;
  explorerLink: (type: "tx" | "address", address: string) => string;
  readonly currency: { name: string; symbol: string; decimals: number };
  readonly supportsProvider: (provider: RpcProvider) => boolean;
  readonly defaultProvider: RpcProvider;
  readonly browserProvider: "public" | "umi";
  readonly providers: RpcProvider[];
  readonly provider: (provider?: RpcProvider) => A extends "EVM" ? { url: string; rateLimits: RpcRateLimits } : never;
  readonly publicRateLimits: A extends "EVM" ? RpcRateLimitsOverride : never; // Rate limits for public RPC
  readonly publicSupportsIpv6: A extends "EVM" ? boolean : never; // Whether the viem default public RPC has native IPv6
  readonly token: Token;
  readonly blockGasLimit: A extends "EVM" ? bigint : never;
  readonly txGasLimit?: A extends "EVM" ? bigint : never; // Protocol-level per-transaction gas cap, if lower than the block gas limit (e.g. EIP-7825)
  readonly maxTxGasLimit: A extends "EVM" ? bigint : never; // min(txGasLimit, blockGasLimit) when txGasLimit is set; otherwise blockGasLimit
  readonly chargesGasLimit?: A extends "EVM" ? boolean : never; // Fee is the declared gas limit, not gas used. simulateBlocks.gasUsed echoes the call gas.
  readonly contracts: A extends "EVM" ? Partial<Record<Contract, Address>> : never;
  readonly shotgun?: A extends "EVM" ? Shotgun : never; // Shotgun broadcast mode for fast-block chains
  readonly mintingConfig?: A extends "EVM" ? { estimateGasLimitOnTime: boolean } : never;
  readonly eip7702?: A extends "EVM" ? boolean : never; // Whether the chain supports EIP-7702 (Pectra)
  readonly maxAuthsPerTx: A extends "EVM" ? number : never; // Per-tx authorization list cap. Number.MAX_SAFE_INTEGER when the chain sets none
  readonly ethSendCapabilities: A extends "EVM" ? EthSendCapabilities : never;
  readonly knownTokens?: Record<string, StableCoinContract>;
};


type ChainParams<
  A extends ChainArch,
  T extends string,
  V extends ViemChain,
  B extends boolean,
> = {
  readonly tag: T;
  readonly name: string;
  readonly logo: IconString;
  readonly logoDark?: IconString;
  readonly arch: A;
  readonly blockTime: number; // Average block time in milliseconds
  readonly subBlockTime?: number; // Sub-block / pre-confirmation interval in ms (e.g. Flashblocks, mini-blocks)
  readonly testnet?: B;
  readonly currency: { name: string; symbol: string; decimals: number };
  readonly browserProvider: "public" | "umi";
  readonly publicRateLimits?: RpcRateLimitsOverride; // Rate limits for the chain
  readonly contracts?: A extends "EVM" ? Partial<Record<Contract, Address>> : never;
  readonly shotgun?: A extends "EVM" ? Omit<Shotgun, "mode"> & { mode?: ShotgunMode } : never; // Shotgun broadcast mode for fast-block chains
  readonly eip7702?: A extends "EVM" ? boolean : never;
  readonly knownTokens?: Record<string, StableCoinContract>;
} & (A extends "EVM" ? {
  l2: boolean;
  id: number;
  viem: V;
  opStack?: boolean;
  zkSync?: boolean;
  fixedBaseFeePerGas?: bigint; // Minimum base fee per gas, if applicable
  minPriorityFeePerGas: bigint; // Minimum priority fee per gas, if applicable
  txOrderingStrategy: "FCFS" | "GAS";
  blockGasLimit: bigint;
  txGasLimit?: bigint; // Protocol-level per-transaction gas cap, if lower than the block gas limit (e.g. EIP-7825)
  chargesGasLimit?: boolean; // Fee is the declared gas limit, not gas used (e.g. Monad)
  maxAuthsPerTx?: number; // Per-tx authorization list cap enforced by the chain's txpool, if any (e.g. Monad)
  mintingConfig?: { estimateGasLimitOnTime: boolean };
  ethSendCapabilities?: EthSendCapabilities;
  publicSupportsIpv6: boolean; // Whether the viem default public RPC has native IPv6
} : object);

const createChain = <
  A extends ChainArch,
  T extends string,
  V extends ViemChain = ViemChain,
  B extends boolean = false,
>(params: ChainParams<A, T, V, B>) => {


  const { publicRateLimits, browserProvider } = params;
  const evmParams = params as ChainParams<"EVM", T, V, B>;

  return {
    ...params,
    logoDark: params.logoDark ?? params.logo,
    ...((() => {
      if (params.arch !== "EVM") return {};
      const { opStack = false, zkSync = false, ethSendCapabilities = {} } = evmParams;
      return {
        opStack,
        zkSync,
        ethSendCapabilities,
        ...(evmParams.shotgun ? { shotgun: { mode: "regular" as const, ...evmParams.shotgun } } : {}),
      };
    })()),
    testnet: params.testnet ?? false,
    explorerLink: (type: "tx" | "address", address: string) =>
      `${(params as any).viem.blockExplorers.default.url}/${type === "tx" ? "tx" : "address"}/${address}`,
    supportsProvider: (provider: RpcProvider) => {

      if (provider === "public") return true; // Always supported
      if (provider === "umi") return platform === "browser" && params.browserProvider === "umi";

      const envKey = (() => {
        if (provider === "drpc") return `${params.tag}_DRPC_HTTP_URL`;
        if (provider === "ankr") return `${params.tag}_ANKR_HTTP_URL`;
        if (provider === "sequencer") return `${params.tag}_SEQUENCER_HTTP_URL`;
        return undefined;
      })();

      return process.env[envKey ?? ""] !== undefined;
    },
    browserProvider: browserProvider ?? "public",
    get defaultProvider() {
      if (platform === "browser") return browserProvider ?? "public";
      for (const provider of ["drpc", "ankr", "public"] as const) {
        if (this.supportsProvider(provider)) return provider;
      }
    },
    get providers() {
      return PROVIDERS.filter(provider => this.supportsProvider(provider));
    },
    provider(provider?: RpcProvider) {

      provider ??= this.defaultProvider;

      if (!this.supportsProvider(provider)) {
        throw new UnsupportedProviderError(provider);
      }

      if (provider === "public") {
        const rateLimits = buildRateLimits(this.publicRateLimits);
        return {
          url: this.viem.rpcUrls.default.http[0],
          rateLimits,
        };
      }

      if (provider === "umi") {
        const { stage } = getCrossPlatformInfo();
        const url = stage === "local" ?
          `http://localhost:2500/rpc/${params.tag}` : stage === "dev" ?
            `https://api.dev.umi.bot/rpc/${params.tag}` :
            `https://api.umi.bot/rpc/${params.tag}`;
        return {
          url,
          rateLimits: buildRateLimits({
            scope: "global",
            defaultLimit: 50
          }),
        };
      }

      const defaultLimit = parseQuota(process.env[`${provider.toUpperCase()}_RATE_LIMIT_QUOTA`]);
      const scope = process.env[`${provider.toUpperCase()}_RATE_LIMIT_SCOPE`] as "chain" | "global";
      const mode = process.env[`${provider.toUpperCase()}_RATE_LIMIT_MODE`] as "request" | "operation";

      return {
        url: process.env[`${params.tag}_${provider.toUpperCase()}_HTTP_URL`],
        rateLimits: buildRateLimits({
          scope,
          mode,
          defaultLimit,
        }),
      };

    },
    publicRateLimits: {
      scope: publicRateLimits?.scope ?? "chain",
      mode: publicRateLimits?.mode ?? "request",
      defaultLimit: publicRateLimits?.defaultLimit ?? 50,
      methods: publicRateLimits?.methods ?? {},
      windowSize: publicRateLimits?.windowSize ?? 1000,
    },
    token: (() : Token => {

      const {
        symbol,
        name,
        decimals,
        address,
      } = (() => {
        if (params.arch === "EVM") {
          const { viem } = evmParams;
          return {
            symbol: viem.nativeCurrency.symbol,
            name: viem.nativeCurrency.name,
            decimals: viem.nativeCurrency.decimals,
            address: zeroAddress,
          };
        }
        throw new Error(`Missing token implementation for chain ${params.tag}`);
      })();

      return {
        address,
        symbol,
        name,
        decimals,
        type: "coin",
        standard: "native",
      };
    })(),
    ...((() => {
      if (params.arch !== "EVM") return {};
      const { blockGasLimit, txGasLimit, maxAuthsPerTx } = evmParams;
      return {
        blockGasLimit,
        txGasLimit,
        maxTxGasLimit: txGasLimit !== undefined && txGasLimit < blockGasLimit
          ? txGasLimit
          : blockGasLimit,
        maxAuthsPerTx: maxAuthsPerTx ?? Number.MAX_SAFE_INTEGER,
      };
    })()),
    contracts: evmParams.contracts ?? {},
  } as Chain<A, T, B>;
};


export const CHAINS = [

  createChain({ // Ethereum
    tag: "ETHEREUM" as const,
    name: "Ethereum",
    logo: "url:/images/logos/chains/ethereum.png",
    arch: "EVM",
    blockTime: 12_000,
    txOrderingStrategy: "GAS",
    minPriorityFeePerGas: parseGwei("0.1"),
    mintingConfig: { estimateGasLimitOnTime: true },
    l2: false,
    viem: mainnet,
    id: mainnet.id,
    currency: mainnet.nativeCurrency,
    browserProvider: "umi",
    publicSupportsIpv6: true,
    publicRateLimits: {
      // Measured on ethereum.reth.rs: 429 -32005 at 1000 rps after holding 500.
      defaultLimit: 500,
    },
    eip7702: true, // Live since the Pectra upgrade (2025-05-07). https://blog.ethereum.org/2025/04/23/pectra-mainnet
    blockGasLimit: 60000000n, // Raised from 45M to 60M in Fusaka (EIP-7935)
    txGasLimit: EIP_7825_TX_GAS_CAP, // EIP-7825, live since Fusaka (2025-12-03)
    contracts: {
      Lens: "0xf121A2369Afa486967a707cfA4b06825653373B0",
      Disperse: "0x72f075f4FCc41467fB930E09B80e3b2cf7616204",
      Bundler: "0x5aae7d748043ecf890b4b730168c5cd332140f15",
      AccountDelegate: "0xc3e26e409340bec05c5cf2703a74477bc75f3321",
    },
    knownTokens: {
      "0xA0b86991c6218b36c1d19D4a2e9Eb0cE3606eB48": { symbol: "USDC", icon: "icon:i-cryptocurrency-color-usdc" },
      "0xdAC17F958D2ee523a2206206994597C13D831ec7": { symbol: "USDT", icon: "icon:i-token-branded-usdt" },
    },
  }),

  createChain({ // Abstract
    tag: "ABSTRACT" as const,
    name: "Abstract",
    logo: "url:/images/logos/chains/abstract.png",
    arch: "EVM",
    blockTime: 500,
    txOrderingStrategy: "FCFS",
    fixedBaseFeePerGas: parseGwei("0.1"), // Normally 0.045gwei
    minPriorityFeePerGas: 0n,
    l2: true,
    zkSync: true,
    id: abstract.id,
    testnet: true,
    viem: abstract,
    currency: abstract.nativeCurrency,
    browserProvider: "umi",
    publicSupportsIpv6: false,
    publicRateLimits: {
      // Measured on api.mainnet.abs.xyz: HTML 429 at 1000 rps after holding 500.
      defaultLimit: 500,
    },
    eip7702: false, // ZKsync ZK Stack (EraVM) does not support EIP-7702 yet. https://docs.zksync.io/zksync-network/zksync-os/faqs
    blockGasLimit: 1125899906842624n, // block.gaslimit placeholder on the ZK stack
    txGasLimit: 80_000_000n, // zkSync Era MAX_GAS_PER_TRANSACTION
    contracts: {
      Lens: "0xf121A2369Afa486967a707cfA4b06825653373B0",
      Disperse: "0x72f075f4FCc41467fB930E09B80e3b2cf7616204",
    }
  }),

  createChain({ // Base
    tag: "BASE" as const,
    name: "Base",
    logo: "url:/images/logos/chains/base.png",
    arch: "EVM",
    blockTime: 2_000,
    subBlockTime: 200,
    opStack: true,
    txOrderingStrategy: "GAS",
    fixedBaseFeePerGas: parseGwei("0.005"),
    minPriorityFeePerGas: parseGwei("0.01"),
    l2: true,
    id: base.id,
    viem: base,
    currency: base.nativeCurrency,
    browserProvider: "umi",
    publicSupportsIpv6: true,
    publicRateLimits: {
      // Measured on mainnet.base.org: 429 -32016 "over rate limit" at 500 rps after holding 200.
      defaultLimit: 200,
    },
    eip7702: true, // OP Stack Isthmus hardfork (2025-05-09). https://gov.optimism.io/t/upgrade-proposal-15-isthmus-hard-fork/9804
    blockGasLimit: 150000000n,
    txGasLimit: EIP_7825_TX_GAS_CAP, // EIP-7825, live since Base Azul hardfork (2026-05-28)
    contracts: {
      Lens: "0xf121A2369Afa486967a707cfA4b06825653373B0",
      Disperse: "0x72f075f4FCc41467fB930E09B80e3b2cf7616204",
      AccountDelegate: "0x0382212164b199937e863ae640b5214b1ff8cba0",
      Bundler: "0x2ffdb37cf382103b1e55012eeea1fe96a7569608"
    },
    knownTokens: {
      "0x833589fCD6eDb6E08f4c7C32D4f71b54bdA02913": { symbol: "USDC", icon: "icon:i-cryptocurrency-color-usdc" },
    },
  }),

  createChain({ // Apechain
    tag: "APECHAIN" as const,
    name: "Apechain",
    logo: "url:/images/logos/chains/apechain.png",
    arch: "EVM",
    blockTime: 250,
    txOrderingStrategy: "FCFS",
    fixedBaseFeePerGas: parseGwei("110"),
    minPriorityFeePerGas: 0n,
    l2: false,
    viem: apeChain,
    id: apeChain.id,
    currency: apeChain.nativeCurrency,
    browserProvider: "umi",
    publicSupportsIpv6: true,
    publicRateLimits: {
      // Probe held 5000 reads and 5000 sends with no 429.
      defaultLimit: Infinity,
    },
    eip7702: true, // Arbitrum Orbit chain; inherits EIP-7702 from ArbOS 40 Callisto. https://docs.arbitrum.io/run-arbitrum-node/arbos-releases/arbos40
    ethSendCapabilities: {
      public: { sendRawTransactionConditional: true },
    },
    blockGasLimit: 1125899906842624n,
    contracts: {
      Lens: "0xf121A2369Afa486967a707cfA4b06825653373B0",
      Disperse: "0x72f075f4FCc41467fB930E09B80e3b2cf7616204",
      Bundler: "0xe1d3074a998d7ec0e24b6d9f47e6ae544945f37e",
      AccountDelegate: "0x5ba4ca57cf7c6db2a0b7509904058d085e8c10b3",
    }
  }),

  createChain({ // Avalanche
    tag: "AVAX" as const,
    name: "Avalanche",
    logo: "url:/images/logos/chains/avax.png",
    arch: "EVM",
    blockTime: 1_000,
    txOrderingStrategy: "GAS",
    minPriorityFeePerGas: parseGwei("1"),
    l2: false,
    viem: avalanche,
    id: avalanche.id,
    currency: avalanche.nativeCurrency,
    browserProvider: "umi",
    publicSupportsIpv6: true,
    publicRateLimits: {
      // Reads held 5000 rps. Send then tripped Cloudflare (retry-after 2975s) at 2500 after a large session. 1000 stays under that.
      defaultLimit: 1000,
    },
    eip7702: false, // C-Chain has not activated EIP-7702; pending ACP-209. https://github.com/avalanche-foundation/ACPs/blob/main/ACPs/209-eip7702-style-account-abstraction/README.md
    blockGasLimit: 20000000n,
    contracts: {
      Lens: "0xf121A2369Afa486967a707cfA4b06825653373B0",
      Disperse: "0x72f075f4FCc41467fB930E09B80e3b2cf7616204",
    },
    knownTokens: {
      "0xB97EF9Ef8734C71904D8002F8b6Bc66Dd9c48a6E": { symbol: "USDC", icon: "icon:i-cryptocurrency-color-usdc" },
      "0x9702230A8Ea53601f5cD2dc00fDBc13d4dF4A8c7": { symbol: "USDT", icon: "icon:i-token-branded-usdt" },
    },
  }),

  createChain({ // Arbitrum
    tag: "ARBITRUM" as const,
    name: "Arbitrum",
    logo: "url:/images/logos/chains/arbitrum.png",
    arch: "EVM",
    blockTime: 250,
    txOrderingStrategy: "FCFS",
    fixedBaseFeePerGas: parseGwei("0.02"),
    minPriorityFeePerGas: 0n,
    l2: true,
    id: arbitrum.id,
    viem: arbitrum,
    currency: arbitrum.nativeCurrency,
    browserProvider: "umi",
    publicSupportsIpv6: true,
    publicRateLimits: {
      // Reads held ~750 rps with no 429. Send 429 "Too Many Requests" at 1000 after holding 500.
      defaultLimit: 2000,
      methods: {
        eth_sendRawTransaction: 500,
      },
    },
    eip7702: true, // ArbOS 40 Callisto (activated 2025-06-17). https://docs.arbitrum.io/run-arbitrum-node/arbos-releases/arbos40
    ethSendCapabilities: {
      public: { sendRawTransactionConditional: true },
    },
    blockGasLimit: 1125899906842624n,
    txGasLimit: 32_000_000n, // ArbOS 51 MaxTxGasLimit (EIP-7825 implementation on Arbitrum One)
    contracts: {
      Lens: "0xf121A2369Afa486967a707cfA4b06825653373B0",
      Disperse: "0x72f075f4FCc41467fB930E09B80e3b2cf7616204",
      Bundler: "0x9868089a0fd6fba77db629d48a29a6626202f81a",
      AccountDelegate: "0x9be7b75cdc74abc3eb02581d8f047cf359792e3a",
    },
    knownTokens: {
      "0xaf88d065e77c8cC2239327C5EDb3A432268e5831": { symbol: "USDC", icon: "icon:i-cryptocurrency-color-usdc" },
      "0xFd086bC7CD5C481DCC9C85ebE478A1C0b69FCbb9": { symbol: "USDT", icon: "icon:i-token-branded-usdt" },
    },
  }),

  createChain({ // Hyperliquid
    tag: "HYPERLIQUID" as const,
    name: "Hyperliquid",
    logo: "url:/images/logos/chains/hyperliquid-dark.png",
    logoDark: "url:/images/logos/chains/hyperliquid.png",
    arch: "EVM",
    blockTime: 1_000,
    txOrderingStrategy: "GAS",
    minPriorityFeePerGas: parseGwei("2"),
    l2: false,
    id: hyperliquid.id,
    viem: hyperliquid,
    currency: hyperliquid.nativeCurrency,
    browserProvider: "umi",
    publicSupportsIpv6: false,
    publicRateLimits: {
      // Official: 100 EVM JSON-RPC req/min per IP. https://hyperliquid.gitbook.io/hyperliquid-docs/for-developers/api/rate-limits-and-user-limits
      windowSize: 60_000,
      defaultLimit: 100,
    },
    eip7702: false, // HyperEVM runs the Cancun hardfork (no type-4 tx). https://hyperliquid.gitbook.io/hyperliquid-docs/for-developers/hyperevm
    blockGasLimit: 2000000n, // Small (fast) block limit; big blocks allow up to txGasLimit
    txGasLimit: 30_000_000n, // HyperEVM big-block gas limit (max a single tx can consume)
    contracts: {
      Lens: "0xf121A2369Afa486967a707cfA4b06825653373B0",
      Disperse: "0x72f075f4FCc41467fB930E09B80e3b2cf7616204",
    },
    knownTokens: {
      "0xb88339CB7199b77E23DB6E890353E22632Ba630f": { symbol: "USDC", icon: "icon:i-cryptocurrency-color-usdc" },
    },
  }),

  createChain({ // Monad
    tag: "MONAD" as const,
    name: "Monad",
    logo: "url:/images/logos/chains/monad.png",
    arch: "EVM",
    blockTime: 400,
    txOrderingStrategy: "GAS",
    fixedBaseFeePerGas: parseGwei("100"), // 100 MON-gwei minimum base fee on mainnet
    minPriorityFeePerGas: parseGwei("2"),
    l2: false,
    id: monad.id,
    viem: monad,
    currency: monad.nativeCurrency,
    browserProvider: "umi",
    publicSupportsIpv6: false,
    publicRateLimits: {
      // Official: rpc.monad.xyz QuickNode 25 rps. https://docs.monad.xyz/developer-essentials/network-information
      defaultLimit: 25,
    },
    eip7702: true, // Supported on mainnet (type 0x04). https://docs.monad.xyz/developer-essentials/eip-7702
    // Monad's txpool drops type-4 txs with more than 4 auths (mempool-only):
    // https://github.com/category-labs/monad-bft/blob/master/monad-eth-txpool/src/pool/transaction.rs
    maxAuthsPerTx: 4,
    blockGasLimit: 200_000_000n,
    txGasLimit: 30_000_000n, // Monad protocol per-transaction gas limit
    chargesGasLimit: true, // Fees and simulateBlocks.gasUsed are the gas limit, not consumption
    contracts: {
      Disperse: "0xc96d7b8b2b870cb472ed9b3fa552424268bf8c82",
      Lens: "0x3117bde7b1f014bc8cc3dfb685a89eaf35bf12b9",
      Bundler: "0xfb24621ef7398e9006992eb816da2b01bd44cc78",
      AccountDelegate: "0xfd07391c719145962900662e7de065149411bb63",
    },
    knownTokens: {
      "0x754704Bc059F8C67012fEd69BC8A327a5aafb603": { symbol: "USDC", icon: "icon:i-cryptocurrency-color-usdc" },
    },
  }),

  createChain({ // Ronin
    tag: "RONIN",
    name: "Ronin",
    logo: "url:/images/logos/chains/ronin.png",
    arch: "EVM",
    blockTime: 3_000,
    txOrderingStrategy: "GAS",
    minPriorityFeePerGas: parseGwei("20"),
    l2: false,
    viem: ronin,
    id: ronin.id,
    currency: ronin.nativeCurrency,
    browserProvider: "umi",
    publicSupportsIpv6: true,
    publicRateLimits: {
      // Advertised: 30/s and 100/min; 100/min is the binding cap.
      windowSize: 60_000,
      defaultLimit: 100,
    },
    eip7702: true, // Kotaro hardfork (Feather Fan upgrade, 2026-07-03) ports Pectra EIPs. https://blog.roninchain.com/p/feather-fan-upgrade
    blockGasLimit: 30029295n,
    contracts: {
      Lens: "0xf121A2369Afa486967a707cfA4b06825653373B0",
      Disperse: "0x72f075f4FCc41467fB930E09B80e3b2cf7616204",
      Bundler: "0x9868089a0fd6fba77db629d48a29a6626202f81a",
      AccountDelegate: "0x9be7b75cdc74abc3eb02581d8f047cf359792e3a",
    }
  }),

  createChain({ // Binance Smart Chain
    tag: "BSC" as const,
    name: "BSC",
    logo: "url:/images/logos/chains/bnb.png",
    arch: "EVM",
    blockTime: 750,
    txOrderingStrategy: "GAS",
    minPriorityFeePerGas: parseGwei("0.1"),
    l2: false,
    id: bsc.id,
    viem: bsc,
    currency: bsc.nativeCurrency,
    browserProvider: "umi",
    publicSupportsIpv6: true,
    publicRateLimits: {
      // Measured on 56.rpc.thirdweb.com public-good: 429 after 20 rps / 1s.
      defaultLimit: 20,
    },
    eip7702: true, // Pascal hardfork ports Pectra's EIP-7702 to BSC. https://docs.blockdaemon.com/docs/eip-7702-transaction-batching
    blockGasLimit: 75000000n,
    txGasLimit: EIP_7825_TX_GAS_CAP, // EIP-7825 via BEP-652 (Mendel hardfork)
    contracts: {
      Lens: "0xf121A2369Afa486967a707cfA4b06825653373B0",
      Disperse: "0x72f075f4FCc41467fB930E09B80e3b2cf7616204",
      Bundler: "0x9be7b75cdc74abc3eb02581d8f047cf359792e3a",
      AccountDelegate: "0x88cf019443270d95f069ce430f0835391ce97a28",
    },
    knownTokens: {
      "0x55d398326f99059fF775485246999027B3197955": { symbol: "USDT", icon: "icon:i-token-branded-usdt" },
    },
  }),

  createChain({ // Berachain
    tag: "BERACHAIN" as const,
    name: "Berachain",
    logo: "url:/images/logos/chains/berachain.png",
    arch: "EVM",
    blockTime: 2_000,
    txOrderingStrategy: "GAS",
    minPriorityFeePerGas: 0n,
    l2: false,
    id: berachain.id,
    viem: berachain,
    currency: berachain.nativeCurrency,
    browserProvider: "umi",
    publicSupportsIpv6: false,
    publicRateLimits: {
      // Advertised QuickNode public: 250/min. 429 "250/minute request limit reached"
      windowSize: 60_000,
      defaultLimit: 250,
    },
    eip7702: true, // Bectra upgrade (full Pectra EL, 2025-06-04). https://blog.berachain.com/blog/bectra-is-live-programmable-wallets-on-demand-exits-and-more-on-berachain
    blockGasLimit: 36000000n,
    txGasLimit: EIP_7825_TX_GAS_CAP, // EIP-7825 per-transaction cap
    contracts: {
      Lens: "0xf121A2369Afa486967a707cfA4b06825653373B0",
      Disperse: "0x72f075f4FCc41467fB930E09B80e3b2cf7616204",
      Bundler: "0x9868089a0fd6fba77db629d48a29a6626202f81a",
      AccountDelegate: "0x9be7b75cdc74abc3eb02581d8f047cf359792e3a",
    }
  }),

  createChain({ // Sei
    tag: "SEI" as const,
    name: "Sei",
    logo: "url:/images/logos/chains/sei.png",
    arch: "EVM",
    blockTime: 400,
    txOrderingStrategy: "GAS",
    minPriorityFeePerGas: parseGwei("1"),
    l2: false,
    id: sei.id,
    viem: sei,
    currency: sei.nativeCurrency,
    browserProvider: "umi",
    publicSupportsIpv6: true,
    publicRateLimits: {
      // Official: 10 req/s. https://docs.sei.io/evm
      defaultLimit: 10,
    },
    eip7702: true, // Sei EVM runs Pectra (without blobs); type-4 set-code tx supported. https://docs.sei.io/evm/evm-parity/transaction-types
    blockGasLimit: 12500000n, // Sei EVM block cap (also the effective per-tx cap; no separate EIP-7825 limit)
    contracts: {
      Lens: "0xf121A2369Afa486967a707cfA4b06825653373B0",
      Disperse: "0x72f075f4FCc41467fB930E09B80e3b2cf7616204",
    },
    knownTokens: {
      "0xe15fC38F6D8c56aF07bbCBe3BAf5708A2Bf42392": { symbol: "USDC", icon: "icon:i-cryptocurrency-color-usdc" },
    },
  }),

  createChain({ // MegaETH
    tag: "MEGAETH" as const,
    name: "MegaETH",
    logo: "url:/images/logos/chains/megaeth.png",
    arch: "EVM",
    blockTime: 1_000,
    subBlockTime: 10,
    txOrderingStrategy: "FCFS",
    fixedBaseFeePerGas: parseGwei("0.001"),
    minPriorityFeePerGas: 0n,
    l2: false,
    id: megaeth.id,
    viem: megaeth,
    currency: megaeth.nativeCurrency,
    browserProvider: "umi",
    publicSupportsIpv6: true,
    publicRateLimits: {
      // Official simple-read window 500/10s; send is exempt from read limits.
      // https://docs.megaeth.com/developer-docs/rpc/operations-and-limits
      // Send probe held 5000 requests at ~650 rps with no 429; cap at a confirmed working 500 rps.
      windowSize: 10_000,
      defaultLimit: 500,
      methods: {
        eth_sendRawTransaction: { windowSize: 1000, limit: 500 },
      },
    },
    eip7702: true, // Type-4 (EIP-7702) supported on MegaETH. https://docs.megaeth.com/mega-evme/reference/transaction-types
    blockGasLimit: 10000000000n, // 10B gas per block/tx (separate 200M compute-gas sub-cap not modelled here)
    shotgun: { shots: 4, interval: 20 },
    contracts: {
      Lens: "0xf121A2369Afa486967a707cfA4b06825653373B0",
      Disperse: "0x72f075f4FCc41467fB930E09B80e3b2cf7616204",
      Bundler: "0x9be7b75cdc74abc3eb02581d8f047cf359792e3a",
      AccountDelegate: "0x88cf019443270d95f069ce430f0835391ce97a28",
    },
  }),

  createChain({ // Somnia
    tag: "SOMNIA" as const,
    name: "Somnia",
    logo: "url:/images/logos/chains/somnia.png",
    arch: "EVM",
    blockTime: 100,
    txOrderingStrategy: "FCFS",
    minPriorityFeePerGas: 0n,
    l2: false,
    id: somnia.id,
    viem: somnia,
    currency: somnia.nativeCurrency,
    browserProvider: "public",
    publicSupportsIpv6: false,
    publicRateLimits: {
      // Probe held 5000 reads and 5000 sends with no 429.
      defaultLimit: Infinity,
    },
    eip7702: true, // Ingot hardfork enabled EIP-7702 on mainnet. https://docs.somnia.network/developer/network-info/somnia-mainnet-releases
    blockGasLimit: 19586964n
  }),

  createChain({ // Sophon
    tag: "SOPHON" as const,
    name: "Sophon",
    logo: "url:/images/logos/chains/sophon.png",
    arch: "EVM",
    blockTime: 1_000,
    txOrderingStrategy: "FCFS",
    fixedBaseFeePerGas: parseGwei("10000"),
    minPriorityFeePerGas: 0n,
    l2: false,
    zkSync: true,
    id: sophon.id,
    viem: sophon,
    currency: sophon.nativeCurrency,
    browserProvider: "public",
    publicSupportsIpv6: false,
    publicRateLimits: {
      // Measured on rpc.sophon.xyz: HTML 429 at 1000 rps after holding 500.
      defaultLimit: 1000,
    },
    eip7702: false, // ZKsync ZK Stack (native AA, no EIP-7702). https://docs.zksync.io/zksync-network/zksync-os/faqs
    blockGasLimit: 1125899906842624n, // block.gaslimit placeholder on the ZK stack
    txGasLimit: 80_000_000n // zkSync Era MAX_GAS_PER_TRANSACTION (in ergs-equivalent EVM gas)
  }),

  createChain({ // Polygon
    tag: "POLYGON" as const,
    name: "Polygon",
    logo: "url:/images/logos/chains/polygon.png",
    arch: "EVM",
    blockTime: 2_000,
    txOrderingStrategy: "GAS",
    minPriorityFeePerGas: parseGwei("25"),
    l2: false,
    id: polygon.id,
    viem: polygon,
    currency: polygon.nativeCurrency,
    browserProvider: "umi",
    publicSupportsIpv6: true,
    publicRateLimits: {
      // Official dRPC public: 120k CU/min, 20 CU/method = 100 rps. https://drpc.org/docs/howitworks/ratelimiting
      defaultLimit: 100,
    },
    eip7702: true, // Bhilai hardfork brought Pectra's EIP-7702 to Polygon PoS. https://docs.blockdaemon.com/docs/eip-7702-transaction-batching
    blockGasLimit: 45000000n,
    contracts: {
      Lens: "0xf121A2369Afa486967a707cfA4b06825653373B0",
      Disperse: "0x72f075f4FCc41467fB930E09B80e3b2cf7616204",
      Bundler: "0x9868089a0fd6fba77db629d48a29a6626202f81a",
      AccountDelegate: "0x9be7b75cdc74abc3eb02581d8f047cf359792e3a",
    },
    knownTokens: {
      "0x3c499c542cEF5E3811e1192ce70d8cC03d5c3359": { symbol: "USDC", icon: "icon:i-cryptocurrency-color-usdc" },
      "0xc2132D05D31c914a87C6611C10748AEb04B58e8F": { symbol: "USDT", icon: "icon:i-token-branded-usdt" },
    },
  }),

  createChain({ // Shape
    tag: "SHAPE" as const,
    name: "Shape",
    logo: "url:/images/logos/chains/shape.png",
    arch: "EVM",
    blockTime: 2_000,
    txOrderingStrategy: "GAS",
    minPriorityFeePerGas: 0n,
    l2: true,
    opStack: true,
    id: shape.id,
    viem: shape,
    currency: shape.nativeCurrency,
    browserProvider: "public",
    publicSupportsIpv6: true,
    publicRateLimits: {
      // Measured on mainnet.shape.network: HTTP 429 at 500 rps after holding 200.
      defaultLimit: 200,
    },
    eip7702: true, // OP Stack Superchain chain; inherits EIP-7702 from Isthmus. https://github.com/ethereum-optimism/superchain-registry/blob/main/superchain/configs/mainnet/shape.toml
    blockGasLimit: 30000000n,
    contracts: {
      Lens: "0xf121A2369Afa486967a707cfA4b06825653373B0",
      Disperse: "0x72f075f4FCc41467fB930E09B80e3b2cf7616204",
      Bundler: "0x9868089a0fd6fba77db629d48a29a6626202f81a",
      AccountDelegate: "0x9be7b75cdc74abc3eb02581d8f047cf359792e3a",
    }
  }),

  createChain({ // Soneium
    tag: "SONEIUM",
    name: "Soneium",
    logo: "url:/images/logos/chains/soneium.png",
    logoDark: "url:/images/logos/chains/soneium-dark.png",
    arch: "EVM",
    blockTime: 2_000,
    txOrderingStrategy: "GAS",
    minPriorityFeePerGas: 0n,
    l2: true,
    opStack: true,
    id: soneium.id,
    viem: soneium,
    currency: soneium.nativeCurrency,
    browserProvider: "umi",
    publicSupportsIpv6: true,
    publicRateLimits: {
      // Advertised: 1800/min on rpc.soneium.org
      windowSize: 60_000,
      defaultLimit: 1800,
    },
    eip7702: true, // OP Stack Isthmus hardfork (2025-05-09). https://github.com/ethereum-optimism/optimism/releases/tag/op-node%2Fv1.13.2
    blockGasLimit: 40000000n,
    txGasLimit: EIP_7825_TX_GAS_CAP // EIP-7825 via OP Stack Karst / Upgrade 19 (scheduled 2026-07-08, treated as live)
  }),

  createChain({ // Plasma
    tag: "PLASMA" as const,
    name: "Plasma",
    logo: "url:/images/logos/chains/plasma.png",
    arch: "EVM",
    blockTime: 1_000,
    txOrderingStrategy: "FCFS",
    fixedBaseFeePerGas: 100n, // 100wei in reality its much less (~10wei)
    minPriorityFeePerGas: 0n,
    l2: true,
    id: plasma.id,
    viem: plasma,
    currency: plasma.nativeCurrency,
    browserProvider: "umi",
    publicSupportsIpv6: false,
    publicRateLimits: {
      // Advertised QuickNode public: 25/s. 429 "25/second request limit reached"
      defaultLimit: 25,
    },
    eip7702: true, // Full EIP-7702 compatibility (Ethereum account model). https://www.plasma.to/docs/plasma-chain/network-information/differences-ethereum-and-plasma
    blockGasLimit: 15000000n,
    contracts: {
      Lens: "0xf121A2369Afa486967a707cfA4b06825653373B0",
      Disperse: "0x72f075f4FCc41467fB930E09B80e3b2cf7616204",
    }
  }),

  createChain({ // Robinhood (Arbitrum Orbit / Nitro L2)
    tag: "ROBINHOOD" as const,
    name: "Robinhood",
    logo: "url:/images/logos/chains/robinhood.png",
    arch: "EVM",
    blockTime: 100,
    txOrderingStrategy: "FCFS",
    minPriorityFeePerGas: 0n,
    l2: true,
    id: robinhood.id,
    shotgun: {
      // shots: 20,
      shots: 3,
      // interval: 86,
      interval: 150,
      // mode: "conditional",
      // targetShift: 200
    },
    viem: {
      ...robinhood,
      blockExplorers: {
        ...robinhood.blockExplorers,
        default: {
          name: "Etherscan",
          url: "https://robin.etherscan.io",
          apiUrl: "https://robin.etherscan.io/api",
        },
      },
    },
    currency: robinhood.nativeCurrency,
    browserProvider: "umi",
    publicSupportsIpv6: true,
    publicRateLimits: {
      // Probe completed 5000 reads and 1000 sends with no 429 (latency-bound, ~200 rps).
      defaultLimit: Infinity,
    },
    blockGasLimit: 1125899906842624n,
    eip7702: true,
    ethSendCapabilities: {
      public: { sendRawTransactionConditional: true },
      sequencer: { sendRawTransactionConditional: true },
    },
    contracts: {
      Disperse: "0x72f075f4FCc41467fB930E09B80e3b2cf7616204",
      Lens: "0x9be7b75cdc74abc3eb02581d8f047cf359792e3a",
      AccountDelegate: "0xb308d91ceca24df580908781171ba5c250b4bc07",
      Bundler: "0x5ba4ca57cf7c6db2a0b7509904058d085e8c10b3",
    },
  }),

  createChain({ // Ink
    tag: "INK" as const,
    name: "Ink",
    logo: "url:/images/logos/chains/ink.png",
    arch: "EVM",
    blockTime: 1_000,
    txOrderingStrategy: "GAS",
    minPriorityFeePerGas: 0n,
    l2: true,
    opStack: true,
    id: ink.id,
    viem: ink,
    currency: ink.nativeCurrency,
    browserProvider: "umi",
    publicSupportsIpv6: false,
    publicRateLimits: {
      // Measured on rpc-gel.inkonchain.com: 429 -32016 at ~715 rps after holding ~476 rps.
      defaultLimit: 500,
    },
    eip7702: true, // OP Stack Isthmus hardfork (2025-05-09). https://github.com/ethereum-optimism/optimism/releases/tag/op-node%2Fv1.13.2
    blockGasLimit: 30000000n,
    txGasLimit: EIP_7825_TX_GAS_CAP, // EIP-7825 via OP Stack Karst / Upgrade 19 (scheduled 2026-07-08, treated as live)
    contracts: {
      Disperse: "0xc96d7b8b2b870cb472ed9b3fa552424268bf8c82",
      Lens: "0x3b1955f55a92a54dc0e3d8b347774dab5020233d",
      Bundler: "0x9868089a0fd6fba77db629d48a29a6626202f81a",
      AccountDelegate: "0x9be7b75cdc74abc3eb02581d8f047cf359792e3a",
    },
    knownTokens: {
      "0x2D270e6886d130D724215A266106e6832161EAEd": { symbol: "USDC", icon: "icon:i-cryptocurrency-color-usdc" },
    },
  }),

  createChain({ // Arc
    tag: "ARC" as const,
    name: "Arc",
    logo: "url:/images/logos/chains/arc.png",
    arch: "EVM",
    blockTime: 500,
    txOrderingStrategy: "GAS",
    minPriorityFeePerGas: 0n,
    l2: false,
    id: arc.id,
    viem: {
      ...arc,
      rpcUrls: {
        default: { http: ["https://rpc.mainnet.arc.io"] },
      },
      blockExplorers: {
        default: {
          name: "Arc Explorer",
          url: "https://explorer.arc.io",
          apiUrl: "https://explorer.arc.io/api/v2",
        },
      },
      contracts: {
        multicall3: {
          address: "0xcA11bde05977b3631167028862bE2a173976CA11",
        },
      },
    },
    currency: arc.nativeCurrency,
    browserProvider: "umi",
    publicSupportsIpv6: true,
    publicRateLimits: {
      // Measured on rpc.mainnet.arc.io: 429 -32005 "rate limit exceeded" at 500 rps after holding 300.
      defaultLimit: 300,
    },
    eip7702: true,
    blockGasLimit: 30_000_000n,
    contracts: {
      Lens: "0xc96d7b8b2b870cb472ed9b3fa552424268bf8c82",
      Disperse: "0x72f075f4FCc41467fB930E09B80e3b2cf7616204",
      Bundler: "0xf326ce7b8ceb62ceff021d060bcf3c8ae91992ab",
      AccountDelegate: "0x7213ed7899dd610653f534c36cd99c533f43ad59"
    }
  }),

] as const;

export const EVM_CHAINS         = CHAINS.filter(chain => chain.arch === "EVM");
export const TESTNET_CHAINS     = CHAINS.filter(chain => chain.testnet);
export const L2_CHAINS          = CHAINS.filter(chain => chain.l2);
export const OP_CHAINS          = CHAINS.filter(chain => chain.opStack);
export const ZK_CHAINS          = CHAINS.filter(chain => chain.zkSync);
export const EIP7702_CHAINS     = CHAINS.filter(chain => chain.eip7702);
export const CHAIN_TAGS         = CHAINS.map(chain => chain.tag);
export const L2_CHAIN_TAGS      = L2_CHAINS.map(chain => chain.tag);
export const OP_CHAIN_TAGS      = OP_CHAINS.map(chain => chain.tag);
export const ZK_CHAIN_TAGS      = ZK_CHAINS.map(chain => chain.tag);
export const TESTNET_CHAIN_TAGS = TESTNET_CHAINS.map(chain => chain.tag);
export const EVM_CHAIN_TAGS     = EVM_CHAINS.map(chain => chain.tag);
export const EIP7702_CHAIN_TAGS = EIP7702_CHAINS.map(chain => chain.tag);

const CHAIN_TAG_MAP    = Object.fromEntries(CHAINS.map(chain => [chain.tag, chain])) as Record<string, Chain>;
const EVM_CHAIN_ID_MAP = Object.fromEntries(EVM_CHAINS.map(chain => [chain.id, chain])) as Record<number, Chain>;


export type EvmChain        = typeof EVM_CHAINS[number];
export type ChainTag        = typeof CHAINS[number]["tag"];
export type EvmChainTag     = typeof EVM_CHAINS[number]["tag"];
export type Eip7702ChainTag = typeof EIP7702_CHAIN_TAGS[number];
export type TestnetChainTag = typeof TESTNET_CHAINS[number]["tag"];


export type ChainManagerConfig = {
  provider?: RpcProvider;
  rpcUrl?: string | null;
  transportType?: "default" | "queue";
};

const managers: Partial<Record<string, EvmManager>> = {};
export const getChainManager = <T extends ChainTag>(tag: T, config?: ChainManagerConfig) => {
  const chain = getChain(tag);
  const provider = config?.provider ?? chain.defaultProvider;
  const cacheKey = `${tag}:${provider}`;
  if (managers[cacheKey]) return managers[cacheKey]!;

  managers[cacheKey] = new EvmManager({
    chainTag: chain.tag,
    provider,
    rpcUrl: config?.rpcUrl,
    transportType: config?.transportType,
  });
  return managers[cacheKey]!;
};


/**
 * Return a typed chain object based on the tag.
 * @param tag
 * @returns
 */
export const getChain = <T extends ChainTag>(tag: T): Extract<typeof CHAINS[number], { tag: T }> => {
  const chain = CHAIN_TAG_MAP[tag];
  if (!chain) throw new Error(`Chain '${tag}' not found`);
  return chain as Extract<typeof CHAINS[number], { tag: T }>;
};

/**
 * Return a chain object based on the chain ID.
 * @param id
 * @returns
 */
export const getChainById = (id: number) => {
  const chain = EVM_CHAIN_ID_MAP[id];
  if (!chain) throw new Error(`Chain with ID '${id}' not found`);
  return chain as EvmChain;
};


export const CHAIN_ARCHS = _.uniq(CHAINS.map(chain => chain.arch)) as ChainArch[];


export const getUmiContractAddress = (chainTag: ChainTag, contract: Contract) => {
  const chain = getChain(chainTag);
  const address = chain.contracts[contract];
  if (!address) throw new Error(`Contract '${contract}' not found for chain '${chainTag}'`);

  return address;
};


/**
 * Return true if the chain has the bundle stack contracts registered.
 */
export const hasBundleStack = (chainTag: ChainTag) => {
  const chain = getChain(chainTag);
  return [
    chain.contracts.Bundler,
    chain.contracts.AccountDelegate,
    chain.contracts.Lens,
  ].every(Boolean);
};
